/**
 * My Blogs Page
 * Purpose: Display blogs created by the logged-in user with edit/delete options
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllBlogs, deleteBlog, getCurrentUser } from '../services/api';
import BlogCard from '../components/BlogCard';
import Alert from '../components/Alert';

const MyBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const [currentUser] = useState(() => getCurrentUser());

  const fetchMyBlogs = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Current user:', currentUser);

      // Fetch all blogs first (without filtering by author)
      const response = await getAllBlogs({});
      console.log('All blogs from server:', response.data);

      // Filter client-side by userId
      // Try multiple comparison methods to ensure match
      const myBlogs = response.data.filter((blog) => {
        const blogUserId = blog.userId?._id || blog.userId;
        const currentUserId = currentUser.id;

        console.log('Comparing:', {
          blogUserId: blogUserId?.toString(),
          currentUserId: currentUserId?.toString(),
          match: blogUserId?.toString() === currentUserId?.toString()
        });

        return blogUserId?.toString() === currentUserId?.toString();
      });

      console.log('Filtered my blogs:', myBlogs);
      console.log('My blogs count:', myBlogs.length);

      setBlogs(myBlogs);
    } catch (error) {
      console.error('Error fetching my blogs:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'Failed to fetch your blogs',
      });
      setBlogs([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchMyBlogs();
  }, [fetchMyBlogs]);

  const handleEdit = (blog) => {
    // Navigate to edit page with blog data
    navigate('/edit-blog', { state: { blog } });
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) {
      return;
    }

    try {
      await deleteBlog(blogId);
      setAlert({ type: 'success', message: 'Blog deleted successfully' });

      // Remove deleted blog from state
      setBlogs(blogs.filter((blog) => blog._id !== blogId));
    } catch (error) {
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'Failed to delete blog',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 py-8 pt-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8 animate-fadeIn">
          <h1 className="text-5xl font-bold text-gray-800">✨ My Blogs</h1>
          <button
            onClick={() => navigate('/create-blog')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg transition-smooth font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            ➕ Create New Blog
          </button>
        </div>

        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ type: '', message: '' })}
        />

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">Loading your blogs...</p>
          </div>
        ) : blogs.length === 0 ? (
          /* No Blogs Found */
          <div className="text-center py-12">
            <p className="text-xl text-gray-600 mb-4">
              You haven't created any blogs yet
            </p>
            <button
              onClick={() => navigate('/create-blog')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-lg transition-smooth font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              🚀 Create Your First Blog
            </button>
          </div>
        ) : (
          /* Blogs Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog, index) => (
              <BlogCard
                key={blog._id}
                blog={blog}
                showActions={true}
                onEdit={handleEdit}
                onDelete={handleDelete}
                index={index}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBlogs;
